import { useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Sun, Cloud, MessageSquare, BookOpen, Camera, FileText,
  TrendingUp, Leaf, Settings, LogOut, Menu, X, CalendarDays, Grid3X3, Bird, Beef 
} from 'lucide-react';

// Tab components
import DailyAdvisor from '../components/tabs/DailyAdvisor';
import WeatherAlerts from '../components/tabs/WeatherAlerts';
import FarmChat from '../components/tabs/FarmChat';
import FarmNotebook from '../components/tabs/FarmNotebook';
import Gallery from '../components/tabs/Gallery';
import ReportsPage from '../components/tabs/ReportsPage';
import MarketInsights from '../components/tabs/MarketInsights';
import SustainabilityTracker from '../components/tabs/SustainabilityTracker';
import SeasonPlanner from '../components/tabs/SeasonPlanner';
import GardenPlanner from '../components/tabs/GardenPlanner';
import GardenPage from '../components/tabs/GardenPage';
import PoultryManager from '../components/tabs/PoultryManager';
import LivestockManager from '../components/tabs/LivestockManager';
import LivestockPage from '../components/tabs/LivestockPage';
import SettingsPage from '../components/tabs/SettingsPage';
import AdminDashboard from '../components/tabs/AdminDashboard';

// 404 component for unknown routes within /app
const AppNotFound = () => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="text-5xl font-heading font-bold text-primary/20 mb-4">404</div>
    <h2 className="text-xl font-heading font-bold text-foreground mb-2">Page Not Found</h2>
    <p className="text-muted-foreground mb-4">This section doesn't exist.</p>
    <Button onClick={() => window.location.href = '/app'} className="btn-primary">
      Back to Dashboard
    </Button>
  </div>
);

const tabs = [
  { id: 'advisor', path: '', icon: Sun, label: 'Advisor' },
  { id: 'garden', path: 'garden', icon: Grid3X3, label: 'Garden' },
  { id: 'poultry', path: 'poultry', icon: Bird, label: 'Poultry' },
  { id: 'livestock', path: 'livestock', icon: Beef, label: 'Livestock' },
  { id: 'notebook', path: 'notebook', icon: BookOpen, label: 'Notes' },
  { id: 'weather', path: 'weather', icon: Cloud, label: 'Weather' },
  { id: 'market', path: 'market', icon: TrendingUp, label: 'Market' },
  { id: 'sustainability', path: 'sustainability', icon: Leaf, label: 'Sustain' },
  { id: 'photos', path: 'photos', icon: Camera, label: 'Gallery' },
  { id: 'reports', path: 'reports', icon: FileText, label: 'Reports' },
  { id: 'chat', path: 'chat', icon: MessageSquare, label: 'Chat' },
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentPath = location.pathname.replace('/app/', '').replace('/app', '');
  const activeTab = tabs.find(t => t.path === currentPath)?.id || 'advisor';

  const handleTabChange = (path) => {
    navigate(`/app/${path}`);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border/60">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/fencepost-logo.png?v=6" alt="Fencepost" className="w-8 h-8 rounded-lg" />
            <span className="font-heading font-bold text-lg text-foreground">Fencepost</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-background border-b border-border/60 shadow-lg animate-slide-up max-h-[70vh] overflow-y-auto">
            <nav className="p-4 pb-6 space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid={`mobile-nav-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
              <div className="border-t border-border/60 my-2 pt-2">
                <button
                  onClick={() => { navigate('/app/settings'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
                  data-testid="mobile-nav-settings"
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Settings</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                  data-testid="mobile-nav-logout"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Log out</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <div className="flex flex-1 pt-14 lg:pt-0">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 border-r border-border/60 bg-card fixed left-0 top-0 bottom-0">
          <div className="p-6 border-b border-border/60">
            <div className="flex items-center gap-2 mb-4">
              <img src="/fencepost-logo.png?v=6" alt="Fencepost" className="w-10 h-10 rounded-lg" />
              <span className="font-heading font-bold text-xl text-foreground">Fencepost</span>
            </div>
            <div className="text-sm">
              <p className="font-medium text-foreground truncate">{user?.farm_name}</p>
              <p className="text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid={`desktop-nav-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-border/60 space-y-1">
            <button
              onClick={() => navigate('/app/settings')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === '/app/settings'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid="desktop-nav-settings"
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              data-testid="desktop-nav-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Log out</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 min-h-screen overflow-x-hidden">
          <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto pb-24 lg:pb-8 overflow-x-hidden">
            <Routes>
              <Route index element={<DailyAdvisor />} />
              <Route path="planner" element={<SeasonPlanner />} />
              <Route path="garden" element={<GardenPage />} />
              <Route path="poultry" element={<PoultryManager />} />
              <Route path="livestock" element={<LivestockPage />} />
              <Route path="weather" element={<WeatherAlerts />} />
              <Route path="chat" element={<FarmChat />} />
              <Route path="notebook" element={<FarmNotebook />} />
              <Route path="photos" element={<Gallery />} />
              <Route path="gallery" element={<Navigate to="/app/photos" replace />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="market" element={<MarketInsights />} />
              <Route path="sustainability" element={<SustainabilityTracker />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin" element={<AdminDashboard />} />
              {/* Redirects for short/alternate names */}
              <Route path="sustain" element={<Navigate to="/app/sustainability" replace />} />
              <Route path="notes" element={<Navigate to="/app/notebook" replace />} />
              <Route path="animals" element={<Navigate to="/app/livestock" replace />} />
              <Route path="crops" element={<Navigate to="/app/garden" replace />} />
              <Route path="season" element={<Navigate to="/app/planner" replace />} />
              <Route path="diagnose" element={<Navigate to="/app/notebook" replace />} />
              {/* Catch-all for unknown routes */}
              <Route path="*" element={<AppNotFound />} />
            </Routes>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border" style={{ backgroundColor: 'hsl(var(--background))' }}>
        <div className="flex items-center justify-around py-2 pb-safe" style={{ backgroundColor: 'hsl(var(--background))' }}>
          {tabs.slice(0, 5).map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.path)}
              className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'text-primary'
                  : 'text-muted-foreground'
              }`}
              data-testid={`bottom-nav-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
